import { type ReactElement } from 'react'
import { documentationRootGuid, projectFilesRootGuid } from '@constants'
import { Box, CircularProgress } from '@mui/material'
import DocumentFileRow from '@editor/components/DocumentFileRow'
import useResultsSectionOpen from '@editor/pages/Results2/hooks/useResultsSectionOpen'
import { EmptyState } from '@ui2/feedback'
import { FileDropzone } from '@ui2/forms'
import { Section } from '@ui2/structure'
import { useUploadProjectDocumentationFile } from '@editorHooks'
import { stripDocumentExtension } from '../utils'

export const DocumentsTab = ({
  elementId,
  documents,
  isLoadingDocuments = false,
  disableForm = false,
}: {
  elementId?: string
  documents: FileItem[]
  /** The project files are loaded for the first time */
  isLoadingDocuments?: boolean
  disableForm?: boolean
}): ReactElement => {
  const { mutate: uploadDocument, isLoading: isUploading } =
    useUploadProjectDocumentationFile(documentationRootGuid)
  const documentsSection = useResultsSectionOpen('results2.documents')

  const uploadFiles = (selectedFiles: File[]) => {
    if (!elementId || disableForm || isLoadingDocuments) return

    selectedFiles.forEach((file, index) => {
      uploadDocument({
        guid: '',
        name: stripDocumentExtension(file.name),
        hierarchy_position: documents.length + index,
        element_guid: elementId,
        parent_guid: '',
        mimetype: file.type || 'application/octet-stream',
        type: 'file',
        file,
      })
    })
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Section title="Dateien" collapsible hideBorderBottom {...documentsSection}>
        <Box sx={{ mt: 1, mb: 1.5 }}>
          <FileDropzone
            multiple
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            disabled={!elementId || disableForm || isLoadingDocuments}
            isUploading={isUploading}
            onFilesSelected={uploadFiles}
          />
        </Box>

        <Box>
          {isLoadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : documents.length === 0 ? (
            <EmptyState>Keine Dateien für dieses Bauteil vorhanden</EmptyState>
          ) : (
            documents.map(file => (
              <DocumentFileRow
                key={file.guid}
                file={file}
                rootGuid={projectFilesRootGuid}
                disableForm={disableForm}
              />
            ))
          )}
        </Box>
      </Section>
    </Box>
  )
}
